import { gql } from '@apollo/client';
import IconCircleCheck from '@mnd-frontend/icons/components/CircleCheck';
import IconMinus from '@mnd-frontend/icons/components/Minus';
import { Button } from '@mnd-frontend/ui/publish/components';
import {
  NEUTRAL_WHITE,
  PURPLE_100,
  PURPLE_400,
  PURPLE_600,
  PURPLE_700,
  PURPLE_800,
} from '@mnd-frontend/ui/shared/tokens';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { useCurrentPressroom } from '../../../../../contexts/CurrentPressroomContext';
import { useWidget, WIDGETS } from '../../../../../contexts/WidgetContext';
import {
  useCompleteOnboardingStepMutation,
  useUserOnboardingStepsQuery,
} from '../../../../../generated/hooks';
import { track } from '../../../../../lib/tracker';
import WidgetTrigger from '../WidgetTrigger';
import WidgetWrapper from '../WidgetWrapper';
import { getStepAction, getStepIcon } from './utils';

gql`
  query UserOnboardingSteps($pressroomId: ID!) {
    me {
      id
      user {
        id
        onboarding(pressroomId: $pressroomId) {
          id
          ...OnboardingStep
        }
      }
    }
  }

  fragment OnboardingStep on OnboardingUser {
    id
    step
    text
    completed
  }

  mutation CompleteOnboardingStep($pressroomId: ID!, $step: OnboardingStep!) {
    userOnboardingCompletion(
      input: { step: $step, pressroomId: $pressroomId }
    ) {
      user {
        id
        onboarding(pressroomId: $pressroomId) {
          id
          completed
        }
      }
    }
  }
`;

const WidgetContentWrapper = styled.div`
  display: flex;
  align-items: center;

  p {
    margin: 0;
    margin-left: 10px;
    font-weight: normal;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px;
  background: ${PURPLE_700};
  color: ${NEUTRAL_WHITE};
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  h2 {
    margin: 0;
    font-size: 1.1rem;
  }
`;

const StepsWrapper = styled.div`
  background: ${NEUTRAL_WHITE};
  height: 100%;
  padding: 16px;
`;

const IconWrapper = styled.div<{ $completed?: boolean }>`
  line-height: 0;
  > span {
    min-height: 24px;
    min-width: 24px;
    color: ${({ $completed }) => ($completed ? PURPLE_400 : PURPLE_700)};
  }
`;

const StepRow = styled.div<{ $completed?: boolean }>`
  display: flex;
  align-items: center;
  padding: 8px;
  gap: 16px;
  border-radius: 4px;
  cursor: pointer;

  span {
    text-decoration: ${({ $completed }) => ($completed ? 'line-through' : 'none')};
    color: ${({ $completed }) => ($completed ? PURPLE_400 : PURPLE_800)};
  }

  &:hover {
    background: ${PURPLE_100};
  }
`;

const ProgressWrapper = styled.div`
  width: 100%;
  background: ${PURPLE_600};
  height: 8px;
  border-radius: 4px;
  margin-left: 18px;
`;

const ProgressBar = styled.div<{ $progress: number }>`
  background: ${NEUTRAL_WHITE};
  width: ${({ $progress }) => $progress}%;
  max-width: 100%;
  height: 100%;
  border-radius: 4px;
`;

const OnboardingWidget = () => {
  const [t] = useTranslation();
  const { widget, setWidget, toggleWidget } = useWidget();
  const { pressroomId } = useCurrentPressroom();
  const navigate = useNavigate();
  const hasTracked = useRef(false);

  const { data, loading } = useUserOnboardingStepsQuery({
    variables: { pressroomId },
  });
  const [completeStepMutation] = useCompleteOnboardingStepMutation();
  const steps = data?.me?.user.onboarding?.length ? data.me.user.onboarding : null;

  useEffect(() => track({ event: 'Onboarding Widget: Widget seen' }), []);

  useEffect(() => {
    if (!steps && !hasTracked.current) {
      track({ event: 'Onboarding Widget: Widget seen' });
      hasTracked.current = true;
    }
  }, [steps]);

  if (!steps || loading) return null;

  const totalSteps = steps.length;
  const completedSteps = steps.filter(({ completed }) => completed).length || 0;
  const progress = (completedSteps / steps.length) * 100;
  const normalizedSteps = steps.map(({ step, ...rest }) => ({
    ...rest,
    icon: getStepIcon(step),
    onClick: () => {
      track({
        event: 'Onboarding Widget: Checklist item clicked',
        traits: { completed: rest.completed, widgetItem: step },
      });
      getStepAction({
        step,
        pressroomId,
        completeStepMutation,
        navigate,
      })();
      setWidget(null);
    },
  }));

  return (
    <>
      <WidgetTrigger
        open={widget === WIDGETS.ONBOARDING}
        onClick={() => {
          if (widget !== WIDGETS.ONBOARDING) track({ event: 'Onboarding Widget: Widget opened' });
          toggleWidget(WIDGETS.ONBOARDING);
        }}
      >
        <WidgetContentWrapper>
          <IconCircleCheck />
          <p>
            {completedSteps}/{totalSteps}
          </p>
        </WidgetContentWrapper>
      </WidgetTrigger>
      <WidgetWrapper open={widget === WIDGETS.ONBOARDING}>
        <Header>
          <HeaderRow>
            <h2>{t('onboarding_widget_title')}</h2>
            <Button
              icon={<IconMinus />}
              configuration="text"
              onDark
              aria-label={t('common_close')}
              type="button"
              onClick={() => setWidget(null)}
            />
          </HeaderRow>
          <HeaderRow>
            {completedSteps}/{totalSteps}
            <ProgressWrapper>
              <ProgressBar $progress={progress} />
            </ProgressWrapper>
          </HeaderRow>
        </Header>
        <StepsWrapper>
          {normalizedSteps.map(({ icon, text, completed, onClick }) => (
            <StepRow key={text} $completed={completed} onClick={onClick}>
              <IconWrapper $completed={completed}>{icon}</IconWrapper>
              <span>{text}</span>
            </StepRow>
          ))}
        </StepsWrapper>
      </WidgetWrapper>
    </>
  );
};

export default OnboardingWidget;
